import type {ItemObject, ItemType, TimelineResizeData} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, MIN_ITEM_DURATION, RESELECT_ITEM_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {CornerPoints} from '@Utils/math.util';
import {getCommonAreaOfRectangles} from '@Utils/math.util';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import {AddItem} from '@PosterWhiteboard/page/add-item.class';
import {PageGroups} from '@PosterWhiteboard/page/page-groups.class';
import type {PageGroupsObject} from '@PosterWhiteboard/page/page-groups.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {ReplaceItem} from '@PosterWhiteboard/page/replace-item.class';
import type {StickerItem} from '@PosterWhiteboard/items/sticker-item.class';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {FabricObject, Group} from '@postermywall/fabricjs-2';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import {recalculateTimelineSubRows} from '@Libraries/timeline-sub-rows.library';
import type {DeepPartial} from '@/global';
import cloneDeep from 'lodash/cloneDeep';
import {noop} from '@Utils/general.util';
import {calculateIntroStartTimes, calculateOutroEndTimes, isAnimationDurationApplicableOnItem} from '@PosterWhiteboard/page/page-animation.util';
import {isImageBackground, isVideoBackground} from '@PosterWhiteboard/page/background/background.types';
import {buildColorBackgroundItemObject} from '@PosterWhiteboard/page/background/background-color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {hexToRgba} from '@Utils/color.util';
import {DEFAULT_BACKGROUND_COLOR} from '@PosterWhiteboard/page/page.types';
import {DEFAULT_NON_VIDEO_PAGE_DURATION} from '@PosterWhiteboard/libraries/video-page';
import {getUniqueString} from '@Utils/string.util';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import {getGroupResizedSpan} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import type {TimelineResizeHandle} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {resizeBackgroundItemObjectForPoster} from '@PosterWhiteboard/poster/resize-poster.class';
import type {PosterTypeDimensions} from '@PosterWhiteboard/poster-type/poster-type.types';

export interface PageItemsObject {
  itemsHashMap: Record<any, ItemObject>;
  itemsOrder: string[];
  groups: PageGroupsObject;
}

export class PageItems {
  public itemsHashMap: Record<string, ItemType> = {};
  public page: Page;
  public addItems: AddItem;
  public replaceItems: ReplaceItem;
  public groups: PageGroups;

  public constructor(page: Page) {
    this.page = page;
    this.addItems = new AddItem(page);
    this.replaceItems = new ReplaceItem(page);
    this.groups = new PageGroups(page);
  }

  public getItemIdsInOrder(): string[] {
    const objs = this.page.fabricCanvas.getObjects();
    const itemsInOrder = [];

    for (let i = 0; i < objs.length; i++) {
      if (objs[i].__PMWID) {
        itemsInOrder.push(objs[i].__PMWID);
      }
    }
    return itemsInOrder;
  }

  public getItemsInOrder(): ItemType[] {
    const itemIdsInOrder = this.getItemIdsInOrder();
    const itemsInOrder: ItemType[] = [];

    for (const id of itemIdsInOrder) {
      const item = this.getItem(id);
      if (item) {
        itemsInOrder.push(item);
      }
    }

    return itemsInOrder;
  }

  public getItemOrder(itemId: string): number {
    // This can come from reinit flow before item fabricObject is available

    return this.page.fabricCanvas.getObjects().indexOf(this.itemsHashMap[itemId]?.fabricObject);
  }

  public getBackgrounds(): ItemType[] {
    return this.getItemsInOrder().filter((item) => item.isBackground);
  }

  public hasMultipleBackgrounds(): boolean {
    return this.getBackgrounds().length > 1;
  }

  public getNonBackgroundItemIdsInOrder(): string[] {
    return this.getItemIdsInOrder().filter((id) => {
      const item = this.getItem(id);
      return item !== undefined && !item.isBackground;
    });
  }

  public getActiveBackground(time?: number): ItemType | undefined {
    const t = time ?? this.page.clock.getCurrentTime();
    const bgs = this.getBackgrounds();
    for (let i = bgs.length - 1; i >= 0; i--) {
      const bg = bgs[i];
      if (t >= bg.onPageStartTime && t < bg.getPageEndTime()) {
        return bg;
      }
    }
    return undefined;
  }

  // The single background that is the active selection, or undefined when the
  // selection is empty, a non-background item, or a multi-selection.
  #getSelectedSingleBackground(): ItemType | undefined {
    const selected = this.page.getSelectedItems();
    if (selected.length === 1 && selected[0].isBackground) {
      return selected[0];
    }
    return undefined;
  }

  // Live-item counterpart of the getBackgroundBeingEdited selector: the selected
  // background when a single background is the active selection, else the
  // background under the playhead. Background edits target this so they stay on
  // the chosen background while the clock moves.
  public getBackgroundBeingEdited(): ItemType | undefined {
    return this.#getSelectedSingleBackground() ?? this.getActiveBackground();
  }

  /**
   * Re-selects the background that replaced `previouslySelected` after an undo/redo reconcile.
   * A background type change (e.g. image -> solid) removes the old background and adds a new one
   * under a fresh uid, so the reconcile destroys the selected Fabric object and recreates an
   * unselected one. Carrying the selection onto the restored background mirrors
   * replaceActiveBackground's forward `wasOldBgSelected` behavior. No-op unless a single
   * background was selected and it was actually swapped out for a different one.
   */
  #reselectSwappedBackground(previouslySelected: ItemType | undefined): void {
    if (!previouslySelected || this.itemsHashMap[previouslySelected.uid]) {
      return;
    }
    const restoredBackground = this.getActiveBackground(previouslySelected.onPageStartTime);
    if (restoredBackground) {
      this.page.activeSelection.setActiveObject(restoredBackground.fabricObject);
    }
  }

  // Subtitle selection lives on the owning transcript (activeSubtitleId), mirroring how
  // audio selection lives on audioClips. One subtitle is active across the page at a time,
  // so the owning transcript gets the id and every other transcript is cleared.
  public setSelectedSubtitle(subtitleUid: string): void {
    let changed = false;
    for (const item of Object.values(this.itemsHashMap)) {
      if (item.isTranscript()) {
        const next = item.subtitlesHashmap[subtitleUid] ? subtitleUid : '';
        if (item.activeSubtitleId !== next) {
          item.activeSubtitleId = next;
          changed = true;
        }
      }
    }
    if (changed) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public clearSelectedSubtitle(): void {
    let changed = false;
    for (const item of Object.values(this.itemsHashMap)) {
      if (item.isTranscript() && item.activeSubtitleId !== '') {
        item.activeSubtitleId = '';
        changed = true;
      }
    }
    if (changed) {
      this.page.poster.redux.updateReduxData();
    }
  }

  // The selected subtitle, but only when its transcript is the active selection — so a
  // stale activeSubtitleId on a non-selected transcript is never acted on.
  public getSelectedSubtitleId(): string | undefined {
    const selected = this.page.getSelectedItems();
    if (selected.length === 1 && selected[0].isTranscript() && selected[0].activeSubtitleId) {
      return selected[0].activeSubtitleId;
    }
    return undefined;
  }

  // Returns the bg with the highest onPageStartTime — the canonical "last bg"
  // in the sequential layout. Prefer this over `getBackgrounds()[length - 1]`,
  // which uses fabric z-order and can diverge from start-time order if the
  // user reorders bgs.
  public getLastBackground(): ItemType | undefined {
    const bgs = this.getBackgrounds();
    if (bgs.length === 0) {
      return undefined;
    }
    return bgs.reduce((latest, current) => (current.onPageStartTime > latest.onPageStartTime ? current : latest));
  }

  public hasVideoBackground(): boolean {
    return this.getBackgrounds().some(isVideoBackground);
  }

  // Sets the last background's onPageEndTime to the target value. Page duration
  // is the sum of background durations, so this is the canonical way to grow or
  // shrink the page. Guarded so the last bg can't be shrunk below its own start
  // (happens when earlier bgs already span past `targetEndTime`). No-op if no
  // background exists. Direct mutation only — doesn't fire events or touch redux.
  public setLastBackgroundEndTime(targetEndTime: number): void {
    const lastBg = this.getLastBackground();
    if (!lastBg) {
      return;
    }
    lastBg.setOnPageEndTime(Math.max(lastBg.onPageStartTime + MIN_ITEM_DURATION, targetEndTime));
  }

  // Forces every background to recompute its `fabricObject.visible` against
  // the current scrubber time. Needed after mutating bg on-page spans without
  // moving the clock — the `time:updated` listener wouldn't otherwise fire.
  public refreshBackgroundVisibility(): void {
    for (const bg of this.getBackgrounds()) {
      bg.updateItemVisibility();
    }
  }

  // Re-tiles backgrounds back-to-back from time 0, preserving each background's
  // duration and current onPageStartTime order. Use after deleting a background
  // so survivors fill the gap.
  public compactBackgroundTiming(): void {
    this.#retileBackgrounds(this.#getBackgroundsSortedByStartTime());
  }

  // Tiles the given backgrounds back-to-back from time 0 in array order, keeping
  // each background's current duration unless `durationOverrides` supplies one.
  #retileBackgrounds(orderedBgs: ItemType[], durationOverrides?: Map<string, number>): void {
    let cursor = 0;
    for (const bg of orderedBgs) {
      const duration = durationOverrides?.get(bg.uid) ?? Math.max(MIN_ITEM_DURATION, bg.getPageEndTime() - bg.onPageStartTime);
      bg.onPageStartTime = Number(cursor.toFixed(2));
      bg.setOnPageEndTime(Number((cursor + duration).toFixed(2)));
      cursor += duration;
    }
    this.refreshBackgroundVisibility();
  }

  #getBackgroundsSortedByStartTime(): ItemType[] {
    return this.getBackgrounds().sort((a, b) => a.onPageStartTime - b.onPageStartTime);
  }

  // Moves a background to `targetIndex` within the start-time-sorted sequence,
  // then re-tiles all backgrounds back-to-back from 0. No-op when the move
  // doesn't change the order.
  public reorderBackground(uid: string, targetIndex: number): void {
    const bg = this.itemsHashMap[uid];
    if (!bg?.isBackground) {
      return;
    }
    const currentOrder = this.#getBackgroundsSortedByStartTime();
    const others = currentOrder.filter((other) => other.uid !== uid);
    const newOrder = [...others.slice(0, targetIndex), bg, ...others.slice(targetIndex)];
    if (newOrder.every((orderedBg, index) => orderedBg.uid === currentOrder[index].uid)) {
      return;
    }
    this.#retileBackgrounds(newOrder);
    this.fitItemsToVisualDuration();
    commitPosterMutation(this.page);
  }

  // Applies a timeline resize to one background, then re-tiles all backgrounds
  // back-to-back from 0 so later ones shift with the resized edge.
  public resizeBackground(uid: string, data: TimelineResizeData): void {
    const resizedBg = this.itemsHashMap[uid];
    if (!resizedBg?.isBackground) {
      return;
    }
    if (resizedBg.isVideo()) {
      this.#fitVideoBackgroundTrimToResize(resizedBg, data);
    }
    const resizedDuration = Math.max(MIN_ITEM_DURATION, data.endTime - data.startTime);
    this.#retileBackgrounds(this.#getBackgroundsSortedByStartTime(), new Map([[uid, resizedDuration]]));
    this.fitItemsToVisualDuration();
    commitPosterMutation(this.page);
  }

  // A bg video's on-page span is derived from its trim and speed (getPageEndTime),
  // so a speed change resizes that span without moving the stored times of later
  // bgs — leaving a gap (speed up) or an overlap (slow down). Re-tile the sequence
  // edge-to-edge and re-fit items to the new visual duration. `undoable: false`
  // skips the history snapshot for mid-drag ticks.
  public retileAfterBackgroundSpanChange(undoable: boolean): void {
    this.compactBackgroundTiming();
    this.fitItemsToVisualDuration();
    commitPosterMutation(this.page, {skipHistory: !undoable});
  }

  // Non-looping video bgs derive getPageEndTime() from the source trim
  // (startTime/endTime). Without updating the trim on resize, the polymorphic
  // toObject() serializes the full playable span and the resize snaps back.
  #fitVideoBackgroundTrimToResize(resizedBg: VideoItem, data: TimelineResizeData): void {
    const {speed, startTime: trimStart, endTime: trimEnd, duration: sourceDuration} = resizedBg;
    const originalStartTime = resizedBg.onPageStartTime;
    const originalEndTime = resizedBg.getPageEndTime();
    // Source duration may be 0 until the video element loads; treat as
    // "unknown ceiling" rather than clamping the trim to 0.
    const sourceCeiling = sourceDuration > 0 ? sourceDuration : Number.POSITIVE_INFINITY;
    // Minimum trim window in source-seconds. Keeps trimEnd > trimStart so
    // getDuration() can't go negative if the on-page floor + speed combine
    // to push the proposed trim past the start.
    const MIN_TRIM_WINDOW = 0.1;

    let newTrimStart = trimStart;
    let newTrimEnd = trimEnd;

    if (data.handle === 'left' && data.startTime !== originalStartTime) {
      const proposed = trimStart + (data.startTime - originalStartTime) * speed;
      newTrimStart = Math.max(0, Math.min(proposed, trimEnd - MIN_TRIM_WINDOW));
    }
    if (data.handle === 'right' && data.endTime !== originalEndTime) {
      const proposed = trimEnd + (data.endTime - originalEndTime) * speed;
      newTrimEnd = Math.min(sourceCeiling, Math.max(proposed, trimStart + MIN_TRIM_WINDOW));
    }

    resizedBg.startTime = Number(newTrimStart.toFixed(2));
    resizedBg.endTime = Number(newTrimEnd.toFixed(2));
  }

  // v1 rule: every non-background item spans the visual span — onPageStartTime is 0
  // (except items with an intro animation, whose animation-derived stagger is preserved)
  // and onPageEndTime is the visual duration (background sum, or max item end time
  // when no backgrounds exist). Items with an applicable outro animation get the
  // outro-adjusted end so the animation completes by the visual duration.
  // Call after any background mutation (resize, reorder, delete, add). Items are
  // capped at the visual ceiling, so an audio clip extending past the last bg
  // does NOT stretch the items. Per-item duration control will replace this in
  // a later phase. Audio clips are deliberately left alone.
  // `targetDuration` overrides the derived visual duration — needed to grow a
  // background-less page, where deriving from item end times is circular.
  public fitItemsToVisualDuration(targetDuration?: number): void {
    const duration = targetDuration ?? this.page.getBackgroundVisualDuration();
    const outroEndTimes = this.pageItemHasOutroAnimation() ? calculateOutroEndTimes(this.itemsHashMap, this.page.fabricCanvas, duration).endTimes : {};

    for (const item of Object.values(this.itemsHashMap)) {
      if (item.isBackground) {
        continue;
      }
      // Multi-slide slideshows and slideshows with an intro delay carry their own
      // on-page duration (driven by slide durations + delay). Force-fitting them to
      // the visual span would clip later slides or stretch a delayed intro.
      if (item.isSlideshow() && item.hasUserControlledDuration()) {
        continue;
      }
      const outroEnd = outroEndTimes[item.uid];
      const endTime = outroEnd !== undefined && isAnimationDurationApplicableOnItem(item) ? outroEnd : duration;
      item.onPageStartTime = item.hasIntroAnimation() && item.onPageStartTime < endTime ? item.onPageStartTime : 0;
      item.setOnPageEndTime(endTime);
      // End time changed without a clock tick, so the time:updated-driven visibility is
      // stale — refresh against the current scrubber time (mirrors refreshBackgroundVisibility).
      item.updateItemVisibility();
    }
  }

  // A page is transparent if any of its backgrounds is transparent — a no-fill solid color
  // background, or an image background with transparency turned on. Design-level on purpose:
  // the download/publish flags and the snapshot format choice must reflect the whole sequence
  // of backgrounds, not just whichever one happens to sit under the playhead.
  public hasAnyTransparentBackground(): boolean {
    return this.getBackgrounds().some((bg) => {
      if (bg.gitype === ITEM_TYPE.RECTANGLE) {
        return bg.getFillObject() === null;
      }
      if (isImageBackground(bg)) {
        return bg.isBackgroundImageTransparencyOn();
      }
      return false;
    });
  }

  // Whether the editor canvas should be left transparent (revealing the checkerboard) rather
  // than painted opaque. This is broader than hasAnyTransparentBackground — it isn't about whether
  // the design exports with alpha, only what backdrop to show: the canvas is transparent by
  // default so any see-through area (a transparent or semi-transparent fill, video alpha, image
  // transparency) shows through, and is opaque only when there is no background at this time or
  // the background is an image with its transparency turned off. Color, gradient and video
  // backgrounds cover the canvas where opaque and let their own alpha show through where not.
  public shouldCanvasBackgroundBeTransparent(time?: number): boolean {
    const bg = this.getActiveBackground(time);
    if (!bg) {
      return false;
    }
    if (isImageBackground(bg)) {
      return bg.isBackgroundImageTransparencyOn();
    }
    return true;
  }

  // Ensures the page has a bg covering the current scrubber time. If a bg is
  // already active at that time, this is a no-op. Otherwise adds a solid white
  // bg that fills the gap between the last existing bg's end and the page's
  // end — so existing bgs aren't overlapped. When the page is empty (no bgs
  // and no duration), falls back to a default 5s bg at time 0.
  public async ensureBackgroundAtCurrentTime(): Promise<void> {
    if (this.getActiveBackground()) {
      return;
    }
    const lastBg = this.getLastBackground();
    const onPageStartTime = lastBg ? lastBg.getPageEndTime() : 0;
    const pageDuration = this.page.getDuration();
    // For sequential bgs, pageDuration is always >= lastBg.getPageEndTime(),
    // and the only way getActiveBackground returns undefined here is when audio
    // extends past the bgs — in which case pageDuration = audioMaxEnd > onPageStartTime.
    // So the gap [onPageStartTime, pageDuration) is always positive.
    const onPageEndTime = pageDuration > 0 ? pageDuration : DEFAULT_NON_VIDEO_PAGE_DURATION;
    const item = await createItemFromObject(
      this.page,
      buildColorBackgroundItemObject(this.page, FillTypes.SOLID, [hexToRgba(DEFAULT_BACKGROUND_COLOR)], {onPageStartTime, onPageEndTime})
    );
    await this.page.addItem(item, {undoable: false, fireEvent: false, updateRedux: false});
    // Commit one history step so the diff-history baseline stays in sync — otherwise later undo/redo patch against a stale snapshot.
    commitPosterMutation(this.page);
  }

  // True when `item` is the page's sole active selection. Used to decide whether a
  // background swap should carry the selection onto the replacement.
  #isTheActiveSelection(item: ItemType | undefined): boolean {
    if (!item) {
      return false;
    }
    const selected = this.page.getSelectedItems();
    return selected.length === 1 && selected[0].uid === item.uid;
  }

  // Used by UI dropdowns that switch bg type (e.g. solid -> image). Targets the background
  // being edited (the selected bg, else the one under the playhead) so the swap lands on the
  // background the user has open rather than whichever bg the clock currently sits on. For
  // attribute-only updates on the same bg, call `bg.updateFromObject(partial)` directly.
  public async replaceActiveBackground(itemObj: DeepPartial<ItemObject>): Promise<ItemType | undefined> {
    const oldBg = this.getBackgroundBeingEdited();
    const wasOldBgSelected = this.#isTheActiveSelection(oldBg);
    let resolvedItemObj: DeepPartial<ItemObject>;
    if (oldBg) {
      // Start the replacement where the old bg started. Callers don't pass times, so
      // the builders' full-page defaults ([0, page duration]) would overlap the other
      // bgs on a multi-bg page and stretch the replacement to the audio length when
      // audio extends past the visuals. A non-video replacement keeps the old slot's
      // duration; a video governs its own (see #fitBackgroundObjectToSlot).
      resolvedItemObj = this.#fitBackgroundObjectToSlot(itemObj, oldBg.onPageStartTime, oldBg.getPageEndTime() - oldBg.onPageStartTime);
      this.doRemoveItem(oldBg.uid);
    } else {
      // No active bg means the scrubber is in a gap past all bgs (audio has extended
      // the page beyond the last bg). Slot the replacement into that gap so it doesn't
      // overlap existing bgs — same rule as ensureBackgroundAtCurrentTime.
      const lastBg = this.getLastBackground();
      const onPageStartTime = lastBg ? lastBg.getPageEndTime() : 0;
      const pageDuration = this.page.getDuration();
      const onPageEndTime = pageDuration > 0 ? pageDuration : DEFAULT_NON_VIDEO_PAGE_DURATION;
      resolvedItemObj = this.#fitBackgroundObjectToSlot(itemObj, onPageStartTime, onPageEndTime - onPageStartTime);
    }
    const item = await createItemFromObject(this.page, resolvedItemObj);
    await this.page.addItem(item, {undoable: false, fireEvent: false, updateRedux: false});
    // A video replacement takes its own natural length, which can differ from the
    // slot it replaced — re-tile so following bgs shift to sit edge-to-edge, then
    // re-fit items to the new visual duration. No-op when the span is unchanged.
    this.compactBackgroundTiming();
    this.fitItemsToVisualDuration();
    if (oldBg?.isPremium() || item.isPremium()) {
      this.page.pageWatermark.refreshWatermark();
    }
    // Records one undo step for the whole swap. The intermediate remove/add ran with
    // undoable: false, so this final commit is the single history snapshot for the type change.
    commitPosterMutation(this.page);
    // The swap drops the old bg's uid, clearing the selection. Carry it onto the
    // replacement so the contextual panel stays on the background (and reopens to the
    // new type's editor) instead of falling back to the default design options.
    if (wasOldBgSelected) {
      await this.page.selectBackground(item.uid);
    }
    return item;
  }

  // Places a replacement bg object at onPageStartTime. A non-video bg fills the
  // [onPageStartTime, onPageStartTime + slotDuration) slot exactly. A video bg
  // instead governs the page duration: it always takes its full natural playable
  // length (source trim / speed), regardless of the slot, and the caller re-tiles
  // following bgs around it. The slot is only used as a fallback when the source
  // duration isn't known yet (video element not loaded).
  #fitBackgroundObjectToSlot(itemObj: DeepPartial<ItemObject>, onPageStartTime: number, slotDuration: number): DeepPartial<ItemObject> {
    if (itemObj.gitype !== ITEM_TYPE.VIDEO) {
      return {...itemObj, onPageStartTime, onPageEndTime: onPageStartTime + slotDuration};
    }
    const videoObj = itemObj as DeepPartial<VideoItemObject>;
    const speed = videoObj.speed !== undefined && videoObj.speed > 0 ? videoObj.speed : 1;
    const trimStart = videoObj.startTime ?? 0;
    // Source duration may be unset until the video element loads — fall back to the slot.
    const sourcePlayable = videoObj.duration !== undefined && videoObj.duration > 0 ? (videoObj.duration - trimStart) / speed : slotDuration;
    const fittedDuration = Math.max(MIN_ITEM_DURATION, sourcePlayable);
    return {
      ...videoObj,
      startTime: trimStart,
      endTime: Number((trimStart + fittedDuration * speed).toFixed(2)),
      onPageStartTime,
      onPageEndTime: Number((onPageStartTime + fittedDuration).toFixed(2)),
    };
  }

  // Duplicates an existing background, slotting the copy directly after the
  // original in the sequential timeline. The copy inherits the original's
  // duration; any subsequent bgs are pushed forward by that duration via
  // `compactBackgroundTiming`. No-op if the uid isn't a known background.
  public async duplicateBackground(uid: string): Promise<ItemType | undefined> {
    const original = this.itemsHashMap[uid];
    if (!original?.isBackground) {
      return undefined;
    }
    const duration = original.getPageEndTime() - original.onPageStartTime;
    // Sort key just after the original — compaction below re-tiles to exact times.
    const insertStart = original.onPageStartTime + 0.001;

    const item = await createItemFromObject(this.page, {
      ...original.toObject(),
      uid: getUniqueString(),
      onPageStartTime: insertStart,
      onPageEndTime: insertStart + duration,
    });
    await this.page.addItem(item, {undoable: false, updateRedux: false, fireEvent: false});
    this.compactBackgroundTiming();
    if (item.isPremium()) {
      this.page.pageWatermark.refreshWatermark();
    }
    commitPosterMutation(this.page);
    return item;
  }

  // Pastes a previously copied background snapshot, slotting it directly after
  // the background active at `currentTime` in the sequential timeline. When no
  // bg covers `currentTime` (scrubber is in the audio-extended gap or page is
  // empty), falls back to appending after the last bg. The original copy is
  // never mutated; the inserted bg gets a fresh uid and the snapshot's duration.
  // `sourcePosterDimensions` are the dimensions of the poster the snapshot was
  // copied from — when they differ from this poster's, the snapshot is refitted
  // so the pasted bg still covers the canvas.
  public async pasteBackgroundAfterActive(snapshot: ItemObject, currentTime: number, sourcePosterDimensions?: PosterTypeDimensions): Promise<ItemType | undefined> {
    const activeBg = this.getActiveBackground(currentTime);
    const duration = Math.max(MIN_ITEM_DURATION, snapshot.onPageEndTime - snapshot.onPageStartTime);

    const resolvedSnapshot = structuredClone(snapshot);
    if (sourcePosterDimensions && (sourcePosterDimensions.width !== this.page.poster.width || sourcePosterDimensions.height !== this.page.poster.height)) {
      resizeBackgroundItemObjectForPoster(resolvedSnapshot, {width: this.page.poster.width, height: this.page.poster.height});
    }

    const item = await createItemFromObject(this.page, {
      ...resolvedSnapshot,
      uid: getUniqueString(),
      // Times overwritten by the re-tile loop below.
      onPageStartTime: 0,
      onPageEndTime: duration,
    });
    await this.page.addItem(item, {undoable: false, updateRedux: false, fireEvent: false});

    // Position-based insertion. A sort-key like `activeBg.end + ε` would land
    // past the next bg whenever bgs are touching (active.end === next.start),
    // which is always true in the back-to-back layout. Splice into the sorted
    // array at activeIndex + 1 instead, then re-tile from that explicit order.
    const sortedBgs = this.#getBackgroundsSortedByStartTime().filter((bg) => bg.uid !== item.uid);
    const activeIndex = activeBg ? sortedBgs.indexOf(activeBg) : -1;
    const insertAt = activeIndex >= 0 ? activeIndex + 1 : sortedBgs.length;
    sortedBgs.splice(insertAt, 0, item);

    this.#retileBackgrounds(sortedBgs);

    if (item.isPremium()) {
      this.page.pageWatermark.refreshWatermark();
    }
    commitPosterMutation(this.page);
    return item;
  }

  // Appends a new background after the last existing one — i.e., sequential
  // add from the timeline "+" dropdown (Uploads / Media / AI / Blank). The new
  // bg starts where the previous last bg ends (or 0 when no bgs exist) and
  // preserves the caller-supplied duration; if the caller didn't specify times,
  // falls back to `DEFAULT_NON_VIDEO_PAGE_DURATION`.
  public async appendBackground(itemObj: DeepPartial<ItemObject>): Promise<ItemType | undefined> {
    const lastBg = this.getLastBackground();
    const onPageStartTime = lastBg ? lastBg.getPageEndTime() : 0;
    const callerStart = itemObj.onPageStartTime;
    const callerEnd = itemObj.onPageEndTime;
    const duration = callerStart !== undefined && callerEnd !== undefined
      ? callerEnd - callerStart
      : DEFAULT_NON_VIDEO_PAGE_DURATION;
    const onPageEndTime = onPageStartTime + duration;

    const item = await createItemFromObject(this.page, {
      ...itemObj,
      onPageStartTime,
      onPageEndTime,
    });
    await this.page.addItem(item, {undoable: false});
    if (item.isPremium()) {
      this.page.pageWatermark.refreshWatermark();
    }
    commitPosterMutation(this.page);
    // Select the newly added background (mirrors duplicateBackgroundAndSelect) so it opens its
    // options and reads as selected in the timeline, rather than leaving the prior selection.
    await this.page.selectBackground(item.uid);
    return item;
  }

  public addItem(newItem: ItemType): void {
    this.itemsHashMap[newItem.uid] = newItem;
  }

  /**
   * Recalculates timelineSubRow for items on the item timeline track (i.e. excluding
   * transcripts and backgrounds, which live on their own tracks) to resolve overlaps
   * and remove empty row gaps.
   */
  public recalculateTimelineSubRows(prioritizedUids: readonly string[] = []): void {
    const items = Object.values(this.itemsHashMap).filter((item) => item.isItemForTimelineItemTrack());
    recalculateTimelineSubRows(items, (item) => item.getPageEndTime(), prioritizedUids);
  }

  // Applies the final state of an item-track drag: new on-page span and,
  // optionally, a new sub-row. When `insertNewRow` is set, items at or below the
  // target row shift down first so the dragged item lands on a fresh row.
  public moveItemOnTimelineTrack(uid: string, move: {startTime: number; endTime: number; newSubRow?: number; insertNewRow?: boolean}): void {
    const item = this.itemsHashMap[uid];
    if (!item) {
      return;
    }

    item.onPageStartTime = Number(move.startTime.toFixed(2));
    item.setOnPageEndTime(Number(move.endTime.toFixed(2)));

    if (move.newSubRow !== undefined) {
      if (move.insertNewRow) {
        for (const otherItem of Object.values(this.itemsHashMap)) {
          if (otherItem.uid !== uid && otherItem.isItemForTimelineItemTrack() && otherItem.timelineSubRow >= move.newSubRow) {
            otherItem.timelineSubRow += 1;
          }
        }
      }
      item.timelineSubRow = move.newSubRow;
    }

    this.recalculateTimelineSubRows();
    commitPosterMutation(this.page);
  }

  // Applies the final state of an item-track resize. Type-specific span rules
  // (video trim, slideshow slide durations) live in each item's
  // applyTimelineResize override.
  public resizeItemOnTimelineTrack(uid: string, data: TimelineResizeData): void {
    const item = this.itemsHashMap[uid];
    if (!item) {
      return;
    }
    item.applyTimelineResize(data);
    this.recalculateTimelineSubRows();
    commitPosterMutation(this.page);
  }

  /**
   * Converts a background into a normal item-track item WITHOUT committing — the
   * caller owns the history entry. The bar keeps its duration at the dropped
   * position/row; the surviving backgrounds re-tile to close the gap. Rebuilds the
   * item through the factory so every isBackground-derived behavior (z-order,
   * controls, looping) is set up by the normal path. Returns false when the uid is
   * not a background, or is a transparent background (nothing to show as an item).
   */
  public async convertBackgroundToItem(uid: string, {startTime, subRow, insertNewRow = false}: {startTime: number; subRow: number; insertNewRow?: boolean}): Promise<boolean> {
    const background = this.itemsHashMap[uid];
    if (!background?.isBackground || background.isTransparentBackground()) {
      return false;
    }
    if (insertNewRow) {
      // Dropped in the gap between rows: open the row at the seam, same shift a
      // same-track row insertion (moveItemOnTimelineTrack) applies.
      for (const otherItem of Object.values(this.itemsHashMap)) {
        if (otherItem.isItemForTimelineItemTrack() && otherItem.timelineSubRow >= subRow) {
          otherItem.timelineSubRow += 1;
        }
      }
    }
    const spanDuration = background.getPageEndTime() - background.onPageStartTime;
    const itemObject = {
      ...background.toObject(),
      isBackground: false,
      // toObject serialized the background's pinned-in-place fabric locks; the
      // converted item must be freely movable again.
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
      lockScalingX: false,
      lockScalingY: false,
      onPageStartTime: Number(Math.max(0, startTime).toFixed(2)),
      onPageEndTime: Number((Math.max(0, startTime) + spanDuration).toFixed(2)),
      timelineSubRow: Math.max(0, subRow),
    };
    this.doRemoveItem(uid);
    this.compactBackgroundTiming();

    const newItem = await createItemFromObject(this.page, itemObject);
    // The converted bar must keep the span the user dropped it at — skip the legacy
    // re-fit that would stretch every item to the (now shorter) visual duration.
    // zIndex 0 clamps to just above the backgrounds: the bar WAS the backdrop, so it
    // enters the item stack at the bottom instead of covering everything.
    await this.page.addItem(newItem, {undoable: false, skipItemsRefit: true, zIndex: 0});
    this.recalculateTimelineSubRows([uid]);
    return true;
  }

  /**
   * Converts an item-track item into a background WITHOUT committing — the caller
   * owns the history entry. The bar keeps its duration and enters the background
   * sequence at `insertionIndex`; the sequence re-tiles around it and the artwork is
   * auto-fit to the poster like any added background. Only media that can be a
   * background converts (image, video, rectangle). Returns false otherwise.
   * Item-only styling (animation, border, shadow/glow, link, rotation, roundness) is
   * cleared on the way in — see Item.getBackgroundConversionResets(). A looping
   * video bar stops looping: its trim clamps to one playback pass, so its slot in
   * the sequence shrinks to that single pass (VideoItem's resets override).
   */
  public async convertItemToBackground(uid: string, insertionIndex: number): Promise<boolean> {
    const item = this.itemsHashMap[uid];
    if (!item || item.isBackground || !item.canBeBackground()) {
      return false;
    }
    const backgroundObject = {
      ...item.toObject(),
      ...item.getBackgroundConversionResets(),
      isBackground: true,
      timelineSubRow: 0,
    };
    resizeBackgroundItemObjectForPoster(backgroundObject, {width: this.page.poster.width, height: this.page.poster.height});
    this.doRemoveItem(uid);

    const newBackground = await createItemFromObject(this.page, backgroundObject);
    await this.page.addItem(newBackground, {undoable: false, skipItemsRefit: true});

    // Slot the new background into the sequence at the drop index and re-tile
    // everything back-to-back. The converted bar's slot comes from its own resolved
    // span (the retile default) — for a video that is the trim clamped at conversion,
    // for everything else the duration the bar was dropped with.
    const others = this.#getBackgroundsSortedByStartTime().filter((bg) => bg.uid !== uid);
    const boundedIndex = Math.max(0, Math.min(insertionIndex, others.length));
    const newOrder = [...others.slice(0, boundedIndex), newBackground, ...others.slice(boundedIndex)];
    this.#retileBackgrounds(newOrder);
    this.recalculateTimelineSubRows();
    return true;
  }

  /**
   * Applies a group drag to the item members WITHOUT committing —
   * `Page.moveTimelineGroup` owns the clamps and the single history commit.
   */
  public applyGroupTimelineMove(uids: string[], deltaTime: number, rowDelta: number): void {
    for (const uid of uids) {
      const item = this.itemsHashMap[uid];
      if (!item) {
        continue;
      }
      const resolvedEndTime = item.getPageEndTime();
      item.onPageStartTime = Number((item.onPageStartTime + deltaTime).toFixed(2));
      item.setOnPageEndTime(Number((resolvedEndTime + deltaTime).toFixed(2)));
      item.timelineSubRow = Math.max(0, item.timelineSubRow + rowDelta);
    }
    // Members claim the rows the gesture placed them on; bystanders re-rack around them.
    this.recalculateTimelineSubRows(uids);
  }

  /**
   * Applies a group resize to the item members WITHOUT committing — each member's
   * span is clamped by its own bounds, then routed through its polymorphic
   * applyTimelineResize (video trim, slideshow slide durations).
   * `Page.resizeTimelineGroup` owns the single history commit.
   */
  public applyGroupTimelineResize(uids: string[], handle: TimelineResizeHandle, deltaTime: number): void {
    for (const uid of uids) {
      const item = this.itemsHashMap[uid];
      if (!item) {
        continue;
      }
      const resizedSpan = getGroupResizedSpan({startTime: item.onPageStartTime, endTime: item.getPageEndTime()}, item.getTimelineResizeBounds(), handle, deltaTime);
      item.applyTimelineResize({handle, startTime: resizedSpan.startTime, endTime: resizedSpan.endTime});
    }
    this.recalculateTimelineSubRows();
  }

  // Splits an item (canvas item or background) at `splitTime` into two items.
  // Uses half-open intervals [start, end) so the pieces tile with no gap.
  public async splitItem(uid: string, splitTime: number): Promise<void> {
    if (await this.applySplit(uid, splitTime)) {
      commitPosterMutation(this.page);
    }
  }

  /**
   * Splits an item (canvas item or background) at `splitTime` into two items WITHOUT
   * committing — the caller owns the history entry, so multi-bar splits land as one
   * undo step. Uses half-open intervals [start, end) so the pieces tile with no gap;
   * the original item keeps its uid as the left half. Returns false when the uid
   * doesn't resolve to an item.
   */
  public async applySplit(uid: string, splitTime: number): Promise<boolean> {
    const item = this.itemsHashMap[uid];
    if (!item) {
      return false;
    }
    // Both halves must be at least MIN_ITEM_DURATION — a split time outside (or at the
    // edge of) the bar's span must never cut it, even when a caller passes a stale uid.
    if (splitTime - item.onPageStartTime < MIN_ITEM_DURATION || item.getPageEndTime() - splitTime < MIN_ITEM_DURATION) {
      return false;
    }

    // Read both halves from the pre-split item — item types whose span is derived
    // (a non-looping video carves its source trim) need the original values.
    const {firstHalf, secondHalf} = item.getSplitObjectsForTime(splitTime);
    const newItemObject = {
      ...item.toObjectWithNewUids(),
      ...secondHalf,
    };

    await item.updateFromObject(firstHalf, {undoable: false, updateRedux: false});
    item.updateItemVisibility();

    const newItem = await createItemFromObject(this.page, newItemObject);
    // The halves tile inside the span the original occupied, so the add cannot change
    // the visual duration — and the post-add re-fit would stomp both halves' spans.
    await this.page.addItem(newItem, {undoable: false, skipItemsRefit: true});
    newItem.updateItemVisibility(this.page.clock.getCurrentTime());
    return true;
  }

  public getItemForFabricObject(fabricItem: FabricObject): ItemType | undefined {
    return this.itemsHashMap[fabricItem.__PMWID];
  }

  public hasVideoItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isVideo()) {
        return true;
      }
    }
    return false;
  }

  public hasMenuItems(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isMenu()) {
        return true;
      }
    }
    return false;
  }

  public hasClickableItem(): boolean {
    return Object.values(this.itemsHashMap).some((item) => {
      return item.hasClickableLink();
    });
  }

  public hasSlideshowItemContainingVideoSlide(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow() && item.slides.hasVideoSlide()) {
        return true;
      }
    }
    return false;
  }

  public hasTranscriptItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isTranscript()) {
        return true;
      }
    }
    return false;
  }

  public hasSlideshowItem(): boolean {
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        return true;
      }
    }
    return false;
  }

  public hasStreamingSlideShowItem(): boolean {
    const itemsIds = Object.keys(this.itemsHashMap);

    for (const itemId of itemsIds) {
      const item = this.itemsHashMap[itemId];
      if (item.isSlideshow() && item.isStreamingItem()) {
        return true;
      }
    }
    return false;
  }

  public hasItem(uid: string): boolean {
    return this.itemsHashMap[uid] !== undefined;
  }

  public getItem(uid: string): ItemType | undefined {
    if (!this.hasItem(uid)) {
      return undefined;
    }

    return this.itemsHashMap[uid];
  }

  /**
   * Applies the same partial-object change to every item in `itemIds` as a single undoable
   * operation. Each item is updated with undo/redux/modified-event suppressed, then history,
   * redux sync, the modified event, and a canvas render all fire exactly once for the whole
   * batch. Used by the group contextual options so a shared/family option row edits all of its
   * target items in one undo step. With a single id it behaves identically to a plain
   * `item.updateFromObject`.
   */
  public async updateItemsFromObject(
    itemIds: string[],
    changes: DeepPartial<ItemObject>,
    {undoable = true, refreshActiveSelection = false, ...passthroughOpts}: {undoable?: boolean} & Partial<UpdateFromObjectOpts> = {}
  ): Promise<void> {
    const items = itemIds.map((id) => this.getItem(id)).filter((item): item is ItemType => item !== undefined);
    if (items.length === 0) {
      return;
    }

    await this.#runWithOptionalActiveSelectionRefresh(refreshActiveSelection, async () => {
      await Promise.all(
        items.map((item) => {
          // Clone per item so no two items store the SAME nested-array reference (e.g. an RGBA
          // fillColor / strokeColor / tintColor). Shared array references across items corrupt the
          // jsondiffpatch-based undo snapshot — the diff mis-attributes array edits and undo restores
          // garbled colours. copyVals assigns incoming arrays by reference, so the clone must happen here.
          return item.updateFromObject(cloneDeep(changes), {
            ...passthroughOpts,
            undoable: false,
            updateRedux: false,
            fireModifiedEvent: false,
          });
        })
      );
    });

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
    this.page.fabricCanvas.requestRenderAll();
  }

  /**
   * Some edits change an item's geometry (e.g. bold/italic resize a text box). When the targets
   * sit inside a multi-item ActiveSelection, fabric won't reflect those changes until the
   * selection is torn down and rebuilt around the updated objects. This discards the selection
   * before `run`, then re-selects the same views afterwards.
   *
   * The discard/reselect are tagged with the reselect event so the selection handlers early-return
   * before syncing redux: the selected set is unchanged, so redux `activeItemIds` stays stable and
   * the contextual panel does not re-render (flash). The modification flag additionally suppresses
   * the deselect side-effects so the group menu stays open. A no-op when not refreshing or when a
   * single item is selected — callers that don't change geometry (e.g. opacity) skip it entirely.
   */
  async #runWithOptionalActiveSelectionRefresh(refreshActiveSelection: boolean, run: () => Promise<void>): Promise<void> {
    const selectedViews = this.page.activeSelection.getSelectedViews() as (FabricObject | Group)[];
    const itemsAreInActiveSelection = refreshActiveSelection && selectedViews.length > 1;

    if (!itemsAreInActiveSelection) {
      await run();
      return;
    }

    const reselectEvent = new MouseEvent(RESELECT_ITEM_EVENT_TYPE);
    this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = true;
    this.page.activeSelection.discardActiveObject(reselectEvent);

    await run();

    // A destructive change (edge effect, border stroke, crop, remove-bg, masking) reinitialises an
    // item's fabric object, replacing the instance captured above with a new one on the canvas. Those
    // stale references would be filtered out (leaving nothing to reselect and dropping the group), so
    // re-resolve each view to its item's CURRENT fabric object by id before reselecting.
    const currentViews = selectedViews.map((view) => {
      const currentFabricObject = view.__PMWID ? this.getItem(view.__PMWID)?.fabricObject : undefined;
      return currentFabricObject ?? view;
    });
    const filteredViews = this.page.getFilteredExistingViews(currentViews);
    if (filteredViews.length) {
      this.page.activeSelection.selectFabricObjects(filteredViews, reselectEvent);
    }
    this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = false;
  }

  /**
   * Like {@link updateItemsFromObject}, but applies a *different* partial-object change per item
   * (keyed by item id) as a single undoable operation. Each item is updated with
   * undo/redux/modified-event suppressed, then history, redux sync, the modified event, and a
   * canvas render all fire exactly once for the whole batch. Used by group options that need to
   * write a per-item value (e.g. relative opacity scaling that preserves each item's ratio).
   */
  public async updateItemsFromObjectMap(
    changesById: Record<string, DeepPartial<ItemObject>>,
    {undoable = true, refreshActiveSelection = false, ...passthroughOpts}: {undoable?: boolean} & Partial<UpdateFromObjectOpts> = {}
  ): Promise<void> {
    const updates = Object.entries(changesById)
      .map(([id, changes]) => {
        return {item: this.getItem(id), changes};
      })
      .filter((update): update is {item: ItemType; changes: DeepPartial<ItemObject>} => {
        return update.item !== undefined;
      });
    if (updates.length === 0) {
      return;
    }

    await this.#runWithOptionalActiveSelectionRefresh(refreshActiveSelection, async () => {
      await Promise.all(
        updates.map(({item, changes}) => {
          // Clone per item: per-item change objects can still share a nested-array reference (e.g. the
          // mixed-type fill path puts the SAME RGBA colour into each item's fillColor), which corrupts
          // the jsondiffpatch undo snapshot. See updateItemsFromObject for the full rationale.
          return item.updateFromObject(cloneDeep(changes), {
            ...passthroughOpts,
            undoable: false,
            updateRedux: false,
            fireModifiedEvent: false,
          });
        })
      );
    });

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    this.page.poster.redux.updateReduxData();
    this.page.fire('modified');
    this.page.fabricCanvas.requestRenderAll();
  }

  public getGettyImageHashedFilenames(excludeItemUids?: Set<string>): string[] {
    const hashedFilenames: string[] = [];

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      if (excludeItemUids?.has(itemId)) {
        continue;
      }
      const item = this.itemsHashMap[itemId];
      // Image backgrounds are gitype IMAGEBACKGROUND, so isImage() is false for them, but they still
      // count toward the Getty image limit. A removed background retains its object but shouldn't count.
      if ((item.isImage() && item.isNonPurchasedGettyImage()) || (item.isImageBackground() && item.isNonPurchasedGettyImage() && !item.isRemoved)) {
        const gettyImageHashedFilename = item.getGettyImageIdentityKey();
        if (gettyImageHashedFilename) {
          hashedFilenames.push(gettyImageHashedFilename);
        }
      } else if (item.isSlideshow()) {
        hashedFilenames.push(...item.slides.getUnpurchasedGettyImageSlideHashedFilenames(excludeItemUids));
      }
    }

    return hashedFilenames;
  }

  public getExtractedGettyStickerHashedFilenames(excludeItemUids?: Set<string>): string[] {
    const hashedFilenames: string[] = [];

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      if (excludeItemUids?.has(itemId)) {
        continue;
      }
      const item = this.itemsHashMap[itemId];
      if (item.isImage() && item.isNonPurchasedExtractedGettySticker() && item.hashedFilename) {
        hashedFilenames.push(item.hashedFilename);
      }
    }

    return hashedFilenames;
  }

  public getGettyVideoHashedFilenames(excludeItemUids?: Set<string>): string[] {
    const hashedFilenames: string[] = [];

    const itemsIds = Object.keys(this.itemsHashMap);
    for (const itemId of itemsIds) {
      if (excludeItemUids?.has(itemId)) {
        continue;
      }
      const item = this.itemsHashMap[itemId];
      if (item.isVideo() && item.hasGettyContent() && item.hashedFilename) {
        hashedFilenames.push(item.hashedFilename);
      } else if (item.isSlideshow()) {
        hashedFilenames.push(...item.slides.getGettyVideoSlideHashedFilenames(excludeItemUids));
      }
    }

    return hashedFilenames;
  }

  public toObject(): PageItemsObject {
    const itemObjects: Record<string, ItemObject> = {};

    for (const [key, item] of Object.entries(this.itemsHashMap)) {
      itemObjects[key] = item.toObject();
    }

    return {
      itemsHashMap: itemObjects,
      // itemsOrder must stay a subset of itemsHashMap keys: removal deletes the map entry before the canvas object (getItemIdsInOrder reads the canvas), so an unfiltered order can carry an id absent from the map and crash mirror selectors that dereference itemsHashMap[id].
      itemsOrder: this.getItemIdsInOrder().filter((itemId) => itemId in itemObjects),
      groups: this.groups.toObject(),
    };
  }

  public toObjectWithNewUids(): PageItemsObject {
    const itemObjects: Record<string, ItemObject> = {};
    const uidMap = new Map<string, string>();
    const groupIdMap = new Map<string, string>();

    for (const [oldUid, item] of Object.entries(this.itemsHashMap)) {
      const itemObject = item.toObjectWithNewUids() as ItemObject;
      const newUid = itemObject.uid;
      uidMap.set(oldUid, newUid);
      if (itemObject.groupId) {
        let newGroupId = groupIdMap.get(itemObject.groupId);
        if (!newGroupId) {
          newGroupId = getUniqueString();
          groupIdMap.set(itemObject.groupId, newGroupId);
        }
        itemObject.groupId = newGroupId;
      }
      itemObjects[newUid] = itemObject;
    }

    const itemsOrder = this.getItemIdsInOrder().map((uid: string) => {
      return uidMap.get(uid) || uid;
    });

    const originalGroups = this.groups.toObject();
    const remappedGroups: PageGroupsObject = {groupsHashMap: {}};
    for (const [oldGroupId, record] of Object.entries(originalGroups.groupsHashMap)) {
      const newGroupId = groupIdMap.get(oldGroupId) ?? oldGroupId;
      remappedGroups.groupsHashMap[newGroupId] = record;
    }

    return {
      itemsHashMap: itemObjects,
      itemsOrder,
      groups: remappedGroups,
    };
  }

  public async updateFromObject(
    itemsObject: DeepPartial<PageItemsObject> = {},
    {updateRedux = true, fireModifiedEvent = true, undoable = true, refreshActiveSelection = false, onItemAddFail = noop, showAddItemLoadingToast = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    // Captured before the reconcile removes it, so an undo/redo type swap can keep it selected.
    const selectedBackgroundBeforeUpdate = refreshActiveSelection ? this.#getSelectedSingleBackground() : undefined;

    // delete items that are in this page but not in the itemsObject
    for (const [uid] of Object.entries(this.itemsHashMap)) {
      if (itemsObject.itemsHashMap?.[uid] === undefined) {
        this.doRemoveItem(uid);
      }
    }

    // Update/Add items from itemsObject
    const updateFromObjectPromises = [];
    const selectedViews = this.page.activeSelection.getSelectedViews() as (FabricObject | Group)[];
    let itemsAreInActiveSelection = false;
    // Tagged so the discard/reselect during a refresh does not propagate to the selection handlers
    // (and thus does not churn the Redux active-item ids), which would otherwise remount the
    // contextual menu — replaying its fade-in and resetting its scroll — on undo/redo of a group.
    const reselectEvent = new MouseEvent(RESELECT_ITEM_EVENT_TYPE);
    if (itemsObject.itemsHashMap) {
      const addItemPromises = [];

      if (refreshActiveSelection && selectedViews.length > 1) {
        itemsAreInActiveSelection = true;
        this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = true;
        this.page.activeSelection.discardActiveObject(reselectEvent);
      }
      for (const [uid, itemObject] of Object.entries(itemsObject.itemsHashMap)) {
        if (itemObject) {
          if (this.itemsHashMap[uid] !== undefined) {
            updateFromObjectPromises.push(
              this.itemsHashMap[uid].updateFromObject(itemObject, {
                updateRedux: false,
                undoable: false,
                fireModifiedEvent: false,
              })
            );
          } else {
            const addItemPromise = new Promise<void>((resolve, reject) => {
              if (showAddItemLoadingToast) {
                showLoading(`addItem${uid}`);
              }
              createItemFromObject(this.page, itemObject)
                .then(async (itemToAdd) => {
                  await this.page.addItem(itemToAdd, {
                    updateRedux: true,
                    undoable: false,
                    fireEvent: false,
                    skipSubRowRecalc: true,
                  });
                  resolve();
                })
                .catch(reject)
                .finally(() => {
                  if (showAddItemLoadingToast) {
                    hideLoading(`addItem${uid}`);
                  }
                });
            });
            if (this.page.poster.mode.isGeneration()) {
              try {
                await addItemPromise;
              } catch (e) {
                console.error(e);
                throw e;
              }
            } else {
              addItemPromises.push(addItemPromise);
            }
          }
        }
      }

      const addItemPromisesResponses = await Promise.allSettled(addItemPromises);
      for (const addItemPromisesResponse of addItemPromisesResponses) {
        if (addItemPromisesResponse.status === 'rejected') {
          console.error(addItemPromisesResponse.reason);
          onItemAddFail();
          break;
        }
      }
    }

    if (itemsObject.itemsOrder) {
      itemsObject.itemsOrder.forEach((itemId, i) => {
        if (itemId) {
          const item = this.itemsHashMap[itemId];
          if (item) {
            this.moveItemTo(item, i);
          }
        }
      });
    }

    if (itemsObject.groups) {
      this.groups.updateFromObject(itemsObject.groups);
    }

    await Promise.all(updateFromObjectPromises);

    // Single pass over the complete reconciled set: identity for valid serialized rows
    // (so a loaded poster keeps the user's saved arrangement), and heals overlapping
    // rows from sources that never assigned them (backend-composed posters).
    this.recalculateTimelineSubRows();

    if (itemsAreInActiveSelection) {
      // Re-resolve to each item's CURRENT fabric object by id: a destructive change (edge effect,
      // border, crop, …) reinitialises the object, so the pre-update references would be stale and
      // filtered out, dropping the group selection.
      const currentViews = selectedViews.map((view) => {
        const currentFabricObject = view.__PMWID ? this.getItem(view.__PMWID)?.fabricObject : undefined;
        return currentFabricObject ?? view;
      });
      const filteredViews = this.page.getFilteredExistingViews(currentViews);
      if (filteredViews.length) {
        this.page.activeSelection.selectFabricObjects(filteredViews, reselectEvent);
      }
      this.page.poster.itemsMultiSelect.isActiveSelectionModificationProcessing = false;
    }

    this.#reselectSwappedBackground(selectedBackgroundBeforeUpdate);

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public moveItemTo(item: ItemType, index: number): void {
    const objs = this.page.fabricCanvas.getObjects();
    let currentIndexForPMWItems = 0;

    for (const [i, obj] of objs.entries()) {
      if (currentIndexForPMWItems === index) {
        this.page.fabricCanvas.moveObjectTo(item.fabricObject, i);
        return;
      }
      if (obj.__PMWID) {
        currentIndexForPMWItems += 1;
      }
    }

    this.page.fabricCanvas.moveObjectTo(item.fabricObject, objs.length);
  }

  public getAllTranscriptItems(): TranscriptItem[] {
    const transcriptItems: TranscriptItem[] = [];

    Object.values(this.itemsHashMap).forEach((item) => {
      if (item.isTranscript()) {
        transcriptItems.push(item);
      }
    });

    return transcriptItems;
  }

  public getMaxSubtitleOnPageEndTime(): number {
    let maxEndTime = 0;
    for (const transcriptItem of this.getAllTranscriptItems()) {
      maxEndTime = Math.max(maxEndTime, transcriptItem.getEndTimeOfLastItem());
    }
    return maxEndTime;
  }

  private onZIndexModified(): void {
    this.groups.repairAllGroupContiguity();
    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  public removeItem(itemUid: string, undoable = true): void {
    const item = this.getItem(itemUid);
    if (item) {
      if ((item.isVideo() || item.isTranscript()) && this.page.poster.mode.isRegen()) {
        getPosterUi().openMessageModal({
          title: window.i18next.t('pmwjs_delete_item_disabled_title'),
          text: window.i18next.t('pmwjs_delete_item_disabled_message'),
        });
        return;
      }

      const groupId = item.groupId;
      this.doRemoveItem(itemUid);
      this.recalculateTimelineSubRows();

      if (groupId) {
        this.groups.autoDissolveIfTooSmall(groupId);
      }
      // Removing a background must keep the sequential layout intact: re-tile the survivors to fill
      // the gap. The backdrop repaint (e.g. deleting the last, transparent background) is handled by
      // the page's 'modified' subscription — see onPageModified.
      if (item.isBackground) {
        this.compactBackgroundTiming();
      }
      // Removing any item can shrink the visual duration (e.g. the longest video),
      // so re-fit every remaining non-bg item to the new span.
      this.fitItemsToVisualDuration();
      if (item.isPremium()) {
        this.page.pageWatermark.refreshWatermark();
      }
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      this.page.fire('modified');
      this.page.poster.redux.updateReduxData();
      void this.page.clampCurrentTime();
    }
  }

  private bringForwardMultipleSelectedItems(items: ItemType[]): void {
    const canvas = this.page.fabricCanvas;
    const objs = canvas.getObjects();
    const sorted = this.page.activeSelection.orderGraphicItems(items);
    const selectedSet = new Set(sorted.map((i) => i.fabricObject));

    const highestZ = objs.indexOf(sorted[sorted.length - 1].fabricObject);
    if (highestZ < 0 || highestZ >= objs.length - 1) {
      return;
    }

    // Skip past selected items so non-contiguous selections still find a real anchor.
    let probeZ = highestZ + 1;
    while (probeZ < objs.length && selectedSet.has(objs[probeZ])) {
      probeZ++;
    }
    if (probeZ >= objs.length) {
      return;
    }

    const aboveItem = this.page.getItemForFabricObject(objs[probeZ]);
    if (!aboveItem) {
      return;
    }

    let topZ = probeZ;
    if (aboveItem.groupId) {
      const groupRange = this.groups.getGroupZRange(aboveItem.groupId);
      if (groupRange) {
        topZ = groupRange.topZ;
      }
    }

    canvas.moveObjectTo(sorted[sorted.length - 1].fabricObject, topZ);
    const newHighestZ = canvas.getObjects().indexOf(sorted[sorted.length - 1].fabricObject);

    // Highest-to-lowest: each move pulls the item down, letting the gap fill from above.
    for (let i = sorted.length - 1; i >= 0; i--) {
      canvas.moveObjectTo(sorted[i].fabricObject, newHighestZ - (sorted.length - 1 - i));
    }
  }

  private sendBackwardsMultipleSelectedItems(items: ItemType[]): void {
    const canvas = this.page.fabricCanvas;
    const objs = canvas.getObjects();
    const sorted = this.page.activeSelection.orderGraphicItems(items);
    const selectedSet = new Set(sorted.map((i) => i.fabricObject));

    const lowestZ = objs.indexOf(sorted[0].fabricObject);
    if (lowestZ <= 0) {
      return;
    }

    let probeZ = lowestZ - 1;
    while (probeZ >= 0 && selectedSet.has(objs[probeZ])) {
      probeZ--;
    }
    if (probeZ < 0) {
      return;
    }

    const belowItem = this.page.getItemForFabricObject(objs[probeZ]);
    if (!belowItem) {
      return;
    }

    let bottomZ = probeZ;
    if (belowItem.groupId) {
      const groupRange = this.groups.getGroupZRange(belowItem.groupId);
      if (groupRange) {
        bottomZ = groupRange.bottomZ;
      }
    }

    canvas.moveObjectTo(sorted[0].fabricObject, bottomZ);
    const newLowestZ = canvas.getObjects().indexOf(sorted[0].fabricObject);

    // Lowest-to-highest: each move pulls the item up, letting the gap fill from below.
    for (let i = 0; i < sorted.length; i++) {
      canvas.moveObjectTo(sorted[i].fabricObject, newLowestZ + i);
    }
  }

  private bringForwardUngroupedItem(item: ItemType): void {
    const canvas = this.page.fabricCanvas;
    const objs = canvas.getObjects();
    const idx = objs.indexOf(item.fabricObject);
    if (idx < 0 || idx >= objs.length - 1) {
      return;
    }

    let targetIdx = idx;
    for (let i = idx + 1; i < objs.length; i++) {
      if (item.fabricObject.isOverlapping(objs[i])) {
        targetIdx = i;
        break;
      }
    }
    if (targetIdx === idx) {
      return;
    }

    const landingItem = this.page.getItemForFabricObject(objs[targetIdx]);
    if (landingItem?.groupId) {
      const groupRange = this.groups.getGroupZRange(landingItem.groupId);
      if (groupRange) {
        targetIdx = groupRange.topZ;
      }
    }

    canvas.moveObjectTo(item.fabricObject, targetIdx);
  }

  private sendBackwardsUngroupedItem(item: ItemType): void {
    const canvas = this.page.fabricCanvas;
    const objs = canvas.getObjects();
    const idx = objs.indexOf(item.fabricObject);
    if (idx <= 0) {
      return;
    }

    let targetIdx = idx;
    for (let i = idx - 1; i >= 0; i--) {
      if (item.fabricObject.isOverlapping(objs[i])) {
        targetIdx = i;
        break;
      }
    }
    if (targetIdx === idx) {
      return;
    }

    const landingItem = this.page.getItemForFabricObject(objs[targetIdx]);
    if (landingItem?.groupId) {
      const groupRange = this.groups.getGroupZRange(landingItem.groupId);
      if (groupRange) {
        targetIdx = groupRange.bottomZ;
      }
    }

    canvas.moveObjectTo(item.fabricObject, targetIdx);
  }

  private bringForwardGroupedItem(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const range = this.groups.getGroupZRange(item.groupId);
    if (!range) {
      return;
    }
    const canvas = this.page.fabricCanvas;
    const itemZ = canvas.getObjects().indexOf(item.fabricObject);
    if (itemZ < 0 || itemZ >= range.topZ) {
      return;
    }
    canvas.moveObjectTo(item.fabricObject, itemZ + 1);
  }

  private sendBackwardsGroupedItem(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const range = this.groups.getGroupZRange(item.groupId);
    if (!range) {
      return;
    }
    const canvas = this.page.fabricCanvas;
    const itemZ = canvas.getObjects().indexOf(item.fabricObject);
    if (itemZ < 0 || itemZ <= range.bottomZ) {
      return;
    }
    canvas.moveObjectTo(item.fabricObject, itemZ - 1);
  }

  private bringToFrontGroupedItem(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const range = this.groups.getGroupZRange(item.groupId);
    if (!range) {
      return;
    }
    this.page.fabricCanvas.moveObjectTo(item.fabricObject, range.topZ);
  }

  private sendToBackGroupedItem(item: ItemType): void {
    if (!item.groupId) {
      return;
    }
    const range = this.groups.getGroupZRange(item.groupId);
    if (!range) {
      return;
    }
    this.page.fabricCanvas.moveObjectTo(item.fabricObject, range.bottomZ);
  }

  public stopItemsAnimation(): void {
    this.getItems().forEach((item) => {
      item.stopItemAnimation();
    });
  }

  private doRemoveItem(itemUid: string): void {
    const item = this.itemsHashMap[itemUid];
    if (!item) {
      return;
    }
    const {fabricObject} = item;
    item.onRemove();
    // Delete from the map before clearing the selection: selection:cleared re-serializes the poster for
    // Redux, and a still-uploading image left in the map would serialize mid-removal without a hashedFilename.
    delete this.itemsHashMap[itemUid];
    this.page.activeSelection.removeItemFromSelection(fabricObject);
    this.page.fabricCanvas.remove(fabricObject);
  }

  public getItems(): ItemType[] {
    return Object.values(this.itemsHashMap);
  }

  public getTextItems(): TextItem[] {
    const items = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isText()) {
        items.push(item);
      }
    }
    return items;
  }

  public getImageItems(): ImageItem[] {
    const items = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isImage()) {
        items.push(item);
      }
    }
    return items;
  }

  public getStickerItems(): StickerItem[] {
    const stickers: StickerItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSticker()) {
        stickers.push(item);
      }
    }
    return stickers;
  }

  public getSlideshowItems(): SlideshowItem[] {
    const slideshowItems: SlideshowItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        slideshowItems.push(item);
      }
    }
    return slideshowItems;
  }

  public getVideoSlides(): VideoSlideItem[] {
    const slideshowItems: VideoSlideItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSlideshow()) {
        slideshowItems.push(...item.slides.getVideoSlides());
      }
    }
    return slideshowItems;
  }

  public getVideoItems(): VideoItem[] {
    const videoItems: VideoItem[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isVideo()) {
        videoItems.push(item);
      }
    }
    return videoItems;
  }

  public getGenerationSeekableItems(): (StickerItem | TranscriptItem)[] {
    const generationSeekableItems: (StickerItem | TranscriptItem)[] = [];
    for (const [, item] of Object.entries(this.itemsHashMap)) {
      if (item.isSticker() || item.isTranscript()) {
        generationSeekableItems.push(item);
      }
    }
    return generationSeekableItems;
  }

  public isVideoItemUsedAsBackground(): boolean {
    if (!this.hasVideoItem()) {
      return false;
    }

    const canvasVertices = this.page.getCanvasCornerPoints();
    const MAX_NON_VIDEO_BACKGROUND_RATIO = 0.7;
    let graphicItemArea;
    let graphicItemAreaRatio;

    const itemsIds = Object.keys(this.itemsHashMap);

    for (const itemId of itemsIds) {
      if (this.itemsHashMap[itemId].isVideo()) {
        const videoItem = this.itemsHashMap[itemId];
        const graphicItemVertices = videoItem.fabricObject.getCornerPoints(videoItem.fabricObject.getCenterPoint());
        // reformatting them according to doPolygonsIntersect() params format requirement
        const graphicItemCornerPoints: CornerPoints = {
          tl: {
            x: graphicItemVertices.tl.x,
            y: graphicItemVertices.tl.y,
          },
          tr: {
            x: graphicItemVertices.tr.x,
            y: graphicItemVertices.tr.y,
          },
          br: {
            x: graphicItemVertices.br.x,
            y: graphicItemVertices.br.y,
          },
          bl: {
            x: graphicItemVertices.bl.x,
            y: graphicItemVertices.bl.y,
          },
        };
        graphicItemArea = getCommonAreaOfRectangles(canvasVertices, graphicItemCornerPoints);
        graphicItemAreaRatio = graphicItemArea / (canvasVertices.bl.x * canvasVertices.bl.y);

        if (graphicItemAreaRatio > MAX_NON_VIDEO_BACKGROUND_RATIO) {
          return true;
        }
      }
    }
    return false;
  }

  public getZIndexMovableTopItem(): ItemType | undefined {
    const objs = this.page.fabricCanvas.getObjects();

    for (let i = objs.length - 1; i >= 0; i--) {
      if (objs[i].__PMWID) {
        const item = this.itemsHashMap[objs[i].__PMWID];
        if (item.canMoveInZIndex()) {
          return this.itemsHashMap[objs[i].__PMWID];
        }
      }
    }
    return undefined;
  }

  public getZIndexMovableBottomItem(): ItemType | undefined {
    const objs = this.page.fabricCanvas.getObjects();

    for (const obj of objs) {
      if (obj.__PMWID) {
        const item = this.itemsHashMap[obj.__PMWID];
        if (item.canMoveInZIndex()) {
          return this.itemsHashMap[obj.__PMWID];
        }
      }
    }
    return undefined;
  }

  public getNumberOfItemsOnPage(): number {
    const itemsIds = Object.keys(this.itemsHashMap);
    return itemsIds.length;
  }

  public canSelectedItemsBeMovedForward = (): boolean => {
    if (this.page.items.getNumberOfItemsOnPage() <= 1) {
      return false;
    }

    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return false;
    }

    const activeSelection = this.page.activeSelection.getActiveObject();
    if (activeSelection?.lockMovementX) {
      return false;
    }

    const onlyItem = selectedItems.length === 1 ? selectedItems[0] : null;
    if (onlyItem?.groupId) {
      const range = this.groups.getGroupZRange(onlyItem.groupId);
      if (!range) {
        return false;
      }
      const itemZ = this.page.fabricCanvas.getObjects().indexOf(onlyItem.fabricObject);
      return itemZ >= 0 && itemZ < range.topZ;
    }

    const topItemZIndex = this.page.items.getZIndexMovableTopItem()?.fabricObject.getZIndex();
    if (topItemZIndex === undefined) {
      return false;
    }

    const sorted = this.page.activeSelection.orderGraphicItems(selectedItems);
    for (const [index, selectedItem] of sorted.entries()) {
      if (selectedItem.fabricObject.getZIndex() !== topItemZIndex - (sorted.length - (index + 1))) {
        return true;
      }
    }

    return false;
  };

  public canSelectedItemsBeMovedBackwards = (): boolean => {
    if (this.page.items.getNumberOfItemsOnPage() <= 1) {
      return false;
    }

    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return false;
    }

    const activeSelection = this.page.activeSelection.getActiveObject();
    if (activeSelection?.lockMovementX) {
      return false;
    }

    const onlyItem = selectedItems.length === 1 ? selectedItems[0] : null;
    if (onlyItem?.groupId) {
      const range = this.groups.getGroupZRange(onlyItem.groupId);
      if (!range) {
        return false;
      }
      const itemZ = this.page.fabricCanvas.getObjects().indexOf(onlyItem.fabricObject);
      return itemZ > range.bottomZ;
    }

    const bottomItemZIndex = this.page.items.getZIndexMovableBottomItem()?.fabricObject.getZIndex();
    if (bottomItemZIndex === undefined) {
      return false;
    }

    const sorted = this.page.activeSelection.orderGraphicItems(selectedItems);
    for (const [index, selectedItem] of sorted.entries()) {
      if (selectedItem.fabricObject.getZIndex() !== bottomItemZIndex + index) {
        return true;
      }
    }

    return false;
  };

  public sendBackwardsSelection(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return;
    }

    const itemIdsInOrder = this.getItemIdsInOrder();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.sendBackwardsGroupedItem(selectedItems[0]);
    } else if (selectedItems.length === 1) {
      this.sendBackwardsUngroupedItem(selectedItems[0]);
    } else {
      this.sendBackwardsMultipleSelectedItems(selectedItems);
    }

    if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
      this.onZIndexModified();
    }
  }

  public bringForwardSelection(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return;
    }

    const itemIdsInOrder = this.getItemIdsInOrder();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.bringForwardGroupedItem(selectedItems[0]);
    } else if (selectedItems.length === 1) {
      this.bringForwardUngroupedItem(selectedItems[0]);
    } else {
      this.bringForwardMultipleSelectedItems(selectedItems);
    }

    if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
      this.onZIndexModified();
    }
  }

  public bringToFrontSelection(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return;
    }

    const itemIdsInOrder = this.getItemIdsInOrder();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.bringToFrontGroupedItem(selectedItems[0]);
    } else {
      // z-ascending so successive bringObjectToFront preserves selection's relative order.
      for (const item of this.page.activeSelection.orderGraphicItems(selectedItems)) {
        this.page.fabricCanvas.bringObjectToFront(item.fabricObject);
      }
    }

    if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
      this.onZIndexModified();
    }
  }

  public sendToBackSelection(): void {
    const selectedItems = this.page.getSelectedZIndexMovableItems();
    if (selectedItems.length === 0) {
      return;
    }

    const itemIdsInOrder = this.getItemIdsInOrder();
    if (selectedItems.length === 1 && selectedItems[0].groupId) {
      this.sendToBackGroupedItem(selectedItems[0]);
    } else {
      // z-descending so successive sendObjectToBack preserves selection's relative order.
      for (const item of this.page.activeSelection.orderGraphicItems(selectedItems).reverse()) {
        this.page.fabricCanvas.sendObjectToBack(item.fabricObject);
      }
    }

    if (this.getItemIdsInOrder().toString() !== itemIdsInOrder.toString()) {
      this.onZIndexModified();
    }
  }

  public moveGroupedItemToPmwZIndex(item: ItemType, targetPmwZIndex: number): boolean {
    if (!item.groupId) {
      return false;
    }
    const members = this.groups.getItemsByGroupId(item.groupId);
    if (members.length === 0) {
      return false;
    }
    const itemIdsInOrder = this.getItemIdsInOrder();
    const memberPmwIndices = members.map((m) => itemIdsInOrder.indexOf(m.uid)).filter((i) => i >= 0);
    if (memberPmwIndices.length === 0) {
      return false;
    }
    const bottomPmw = Math.min(...memberPmwIndices);
    const topPmw = Math.max(...memberPmwIndices);
    const clamped = Math.max(bottomPmw, Math.min(topPmw, targetPmwZIndex));

    const beforeIndex = itemIdsInOrder.indexOf(item.uid);
    this.moveItemTo(item, clamped);
    if (this.getItemIdsInOrder().indexOf(item.uid) === beforeIndex) {
      return false;
    }
    this.onZIndexModified();
    return true;
  }

  public setPmwZOrder(orderedItemIds: string[]): boolean {
    const uniqueOrderedIds = [...new Set(orderedItemIds)];
    const itemIdsInOrderBefore = this.getItemIdsInOrder();
    const currentIdsSet = new Set(itemIdsInOrderBefore);
    const orderedIdsSet = new Set(uniqueOrderedIds);
    const idsToPlace = uniqueOrderedIds.filter((uid) => currentIdsSet.has(uid));
    let nextIdToPlaceIndex = 0;
    const finalOrder = itemIdsInOrderBefore.map((uid) => {
      if (!orderedIdsSet.has(uid)) {
        return uid;
      }
      const placedUid = idsToPlace[nextIdToPlaceIndex];
      nextIdToPlaceIndex += 1;
      return placedUid;
    });
    if (finalOrder.every((uid, index) => uid === itemIdsInOrderBefore[index])) {
      return false;
    }
    finalOrder.forEach((uid, index) => {
      const item = this.getItem(uid);
      if (item) {
        this.moveItemTo(item, index);
      }
    });
    if (this.getItemIdsInOrder().toString() === itemIdsInOrderBefore.toString()) {
      return false;
    }
    this.onZIndexModified();
    return true;
  }

  public getTranscriptItemIds(): string[] {
    const itemsHashMapObject = this.toObject().itemsHashMap;
    const ids: string[] = [];

    for (const [, item] of Object.entries(itemsHashMapObject)) {
      if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
        ids.push(item.uid);
      }
    }

    return ids;
  }

  public updateItemsAnimation(): void {
    // Animation recalc must use the visual (background) duration, not getDuration() — the latter
    // includes audio/subtitles, which would push an animated item's outro end into the audio-only
    // tail past the visual span. fitItemsToVisualDuration sizes items the same way; keeping these
    // in sync is what lets an add-background/undo cycle revert an animated item's end time.
    const visualDuration = this.page.getBackgroundVisualDuration();
    if (this.pageItemHasIntroAnimation()) {
      const introResult = calculateIntroStartTimes(this.itemsHashMap, this.page.fabricCanvas, visualDuration, this.isVideoItemUsedAsBackground());
      for (const [uid, startTime] of Object.entries(introResult.startTimes)) {
        if (this.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.itemsHashMap[uid])) {
          this.itemsHashMap[uid].onPageStartTime = startTime;
        }
      }
      for (const [uid, direction] of Object.entries(introResult.directionUpdates)) {
        this.itemsHashMap[uid]?.introAnimation.copyVals({direction});
      }
    }
    if (this.pageItemHasOutroAnimation()) {
      const outroResult = calculateOutroEndTimes(this.itemsHashMap, this.page.fabricCanvas, visualDuration);
      for (const [uid, endTime] of Object.entries(outroResult.endTimes)) {
        if (this.itemsHashMap[uid] && isAnimationDurationApplicableOnItem(this.itemsHashMap[uid])) {
          this.itemsHashMap[uid].setOnPageEndTime(endTime);
        }
      }
      for (const [uid, direction] of Object.entries(outroResult.directionUpdates)) {
        this.itemsHashMap[uid]?.outroAnimation.copyVals({direction});
      }
    }
  }

  public pageItemHasAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasAnimation());
  }

  public pageItemHasIntroAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasIntroAnimation());
  }

  public pageItemHasOutroAnimation(): boolean {
    return Object.values(this.itemsHashMap).some((item) => item.hasOutroAnimation());
  }

}

export const deleteTranscriptItemWithSubtitleId = (subtitleId: string): void => {
  const items = window.posterEditor?.whiteboard?.getCurrentPage().items.itemsHashMap;
  if (!items) {
    return;
  }
  const transcriptItems = Object.values(items).filter((item) => item.gitype === ITEM_TYPE.TRANSCRIPT) as TranscriptItem[];

  for (const transcriptItem of transcriptItems) {
    for (const subtitle of Object.values(transcriptItem.subtitlesHashmap)) {
      if (subtitle.subtitleUID === subtitleId) {
        window.posterEditor?.whiteboard?.deleteItemById(transcriptItem.uid);
        return;
      }
    }
  }
};

export const deleteSubtitleById = (subtitleId: string): boolean => {
  const items = window.posterEditor?.whiteboard?.getCurrentPage().items.itemsHashMap;
  if (!items) {
    return false;
  }

  const transcriptItems = Object.values(items).filter((item) => item.gitype === ITEM_TYPE.TRANSCRIPT) as TranscriptItem[];

  for (const transcriptItem of transcriptItems) {
    if (transcriptItem.subtitlesHashmap[subtitleId]) {
      return transcriptItem.deleteSubtitle(subtitleId);
    }
  }

  return false;
};
